import type * as api from '@gitbook/api-client';
import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient, flattenGroupedSectionGroups } from '../../../state';
import { SiteSectionGroupIcon } from '../SiteSectionGroupIcon';

export function useMoveSiteExternalLink(props: {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
}) {
    const { organization, site, siteStructure } = props;

    return useEventCallback((siteExternalLink: api.SiteExternalLink) => {
        const groups =
            siteStructure.type === 'sections'
                ? flattenGroupedSectionGroups(siteStructure.structure)
                : [];
        return createPalette({
            search: { placeholder: 'Find a group…' },
            groups: [
                siteExternalLink.sectionGroup
                    ? {
                          key: 'site',
                          commands: [
                              {
                                  key: 'site.root',
                                  title: 'Root of site',
                                  icon: Icon.DocsSite,
                                  trigger: () =>
                                      moveSiteExternalLink({
                                          organization,
                                          site,
                                          siteExternalLink,
                                          parent: null,
                                      }),
                              },
                          ],
                      }
                    : null,
                {
                    key: 'groups',
                    commands: groups
                        .filter((group) => group.id !== siteExternalLink.sectionGroup)
                        .map((group) => ({
                            key: group.id,
                            title: group.title,
                            icon: <SiteSectionGroupIcon siteSectionGroup={group} />,
                            trigger: () =>
                                moveSiteExternalLink({
                                    organization,
                                    site,
                                    siteExternalLink,
                                    parent: group.id,
                                }),
                        })),
                },
            ].filter((group) => group !== null),
        });
    });
}

export async function moveSiteExternalLink(props: {
    organization: api.Organization;
    site: api.Site;
    siteExternalLink: api.SiteExternalLink;
    parent: string | null;
}) {
    const { organization, site, siteExternalLink, parent } = props;
    await toast.catch(async () => {
        await apiClient.siteExternalLinks.updateSiteExternalLinkById(
            organization.id,
            site.id,
            siteExternalLink.id,
            { parent },
            { returnPreference: 'minimal' }
        );
        toast.success('External link moved');
    }, 'Error while moving external link');
}
